import { useState } from 'react';
import { Alert, Button, Grid, NumberInput, Paper, Select, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { StockCountLineResponse } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { parseNum } from '../../../lib/parseNum';
import { useActiveProducts, useLocations, useUpsertStockCountLine } from '../api';
import { formatQty, formatSignedQty } from '../utils';

/** Add/update stock-count lines (BUILD_SPEC.md §8.6: "enter counted quantities
 * per line (expected shown as a read-only snapshot) incl. handling-units
 * counted"). `PUT /silo/counts/:id/lines` is an idempotent upsert keyed on
 * (count, product, location) — picking an existing line's product+location
 * again just updates it, so "add" and "edit" are the same form. */
export function CountLinesEditor({
  countId,
  siteId,
  lines,
  editable,
}: {
  countId: string;
  siteId: string;
  lines: StockCountLineResponse[];
  editable: boolean;
}) {
  const locationsQuery = useLocations(siteId);
  const productsQuery = useActiveProducts();
  const upsertLine = useUpsertStockCountLine(countId);

  const [locationId, setLocationId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [countedQty, setCountedQty] = useState<number | string>('');
  const [handlingUnits, setHandlingUnits] = useState<number | string>('');
  const [note, setNote] = useState('');

  const locations = locationsQuery.data?.items ?? [];
  const products = productsQuery.data?.items ?? [];
  const locationsById = new Map(locations.map((l) => [l.id, l]));
  const productsById = new Map(products.map((p) => [p.id, p]));

  const parsedCountedQty = parseNum(countedQty);
  const parsedHandlingUnits = parseNum(handlingUnits);

  function loadLine(line: StockCountLineResponse) {
    setLocationId(line.locationId);
    setProductId(line.productId);
    setCountedQty(line.countedQty === null ? '' : Number(line.countedQty));
    setHandlingUnits(line.handlingUnitsCounted === null ? '' : line.handlingUnitsCounted);
    setNote(line.note ?? '');
  }

  function clearForm() {
    setLocationId(null);
    setProductId(null);
    setCountedQty('');
    setHandlingUnits('');
    setNote('');
    upsertLine.reset();
  }

  function handleSave() {
    if (!locationId || !productId) return;
    upsertLine.mutate(
      {
        locationId,
        productId,
        countedQty: parsedCountedQty === null ? null : parsedCountedQty.toFixed(3),
        handlingUnitsCounted: parsedHandlingUnits,
        note: note.trim() === '' ? null : note.trim(),
      },
      { onSuccess: clearForm },
    );
  }

  return (
    <>
      {editable && (
        <Paper withBorder radius="md" p="md" mb="md">
          <Title order={4} mb="sm">
            Count a line
          </Title>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Select
                label="Location"
                placeholder="Choose a location"
                data={locations.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
                value={locationId}
                onChange={setLocationId}
                searchable
                disabled={locationsQuery.isLoading}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Select
                label="Product"
                placeholder="Choose a product"
                data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
                value={productId}
                onChange={setProductId}
                searchable
                disabled={productsQuery.isLoading}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 2 }}>
              <NumberInput
                label="Counted qty"
                description="In the product's base unit (kg, each or litres)"
                decimalScale={3}
                value={countedQty}
                onChange={setCountedQty}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 2 }}>
              <NumberInput
                label="Handling units"
                description="Whole packs/pallets/bags counted, not mass"
                decimalScale={0}
                value={handlingUnits}
                onChange={setHandlingUnits}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <TextInput label="Note" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
            </Grid.Col>
          </Grid>
          {upsertLine.isError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />} mt="sm">
              {upsertLine.error instanceof ApiRequestError ? upsertLine.error.message : upsertLine.error?.message}
            </Alert>
          )}
          <Button mt="sm" loading={upsertLine.isPending} disabled={!locationId || !productId} onClick={handleSave}>
            Save line
          </Button>
        </Paper>
      )}

      <Table.ScrollContainer minWidth={720}>
        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Location</Table.Th>
              <Table.Th>Product</Table.Th>
              <Table.Th>Expected</Table.Th>
              <Table.Th>Counted</Table.Th>
              <Table.Th>
                <Tooltip label="Counted minus expected, in the product's base unit">
                  <span>Variance</span>
                </Tooltip>
              </Table.Th>
              <Table.Th>
                <Tooltip label="Expected handling units / counted handling units">
                  <span>Handling (exp/counted)</span>
                </Tooltip>
              </Table.Th>
              <Table.Th>Note</Table.Th>
              {editable && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((line) => {
              const variance = line.countedQty === null ? null : Number(line.countedQty) - Number(line.expectedQty);
              return (
                <Table.Tr key={line.id}>
                  <Table.Td>{locationsById.get(line.locationId)?.name ?? line.locationId}</Table.Td>
                  <Table.Td>{productsById.get(line.productId)?.name ?? line.productId}</Table.Td>
                  <Table.Td>{formatQty(line.expectedQty)}</Table.Td>
                  <Table.Td>{formatQty(line.countedQty)}</Table.Td>
                  <Table.Td>{variance === null ? '—' : formatSignedQty(variance)}</Table.Td>
                  <Table.Td>
                    {line.expectedHandlingUnits ?? '—'} / {line.handlingUnitsCounted ?? '—'}
                  </Table.Td>
                  <Table.Td>{line.note ?? ''}</Table.Td>
                  {editable && (
                    <Table.Td>
                      <Button variant="subtle" size="compact-sm" onClick={() => loadLine(line)}>
                        Edit
                      </Button>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        {lines.length === 0 && (
          <Text c="dimmed" size="sm" mt="sm">
            No lines counted yet.
          </Text>
        )}
      </Table.ScrollContainer>
    </>
  );
}
